// Kebab menu "..." con quick actions por paciente en DashboardKine.
// Sprint 3 — desktop tabla + mobile card usan el mismo componente.
//
// 5 acciones (orden vertical, con separadores cuando aplica):
//   - Ver detalle (siempre)              → navega /paciente/:id
//   - Prescribir rutina (siempre)        → navega /prescribir/:id
//   - Enviar mensaje (disabled)          → "Próximamente"
//   - Copiar invitación (condicional)    → clipboard del link + toast
//   - Compartir por WhatsApp (condicional) → abre InvitacionModal
//
// Los 2 últimos solo aparecen si invitationStatus === 'invited' Y hay
// signupToken. Si el token expiró, el menú igual los muestra — la UX
// "regenerar invitación" vive en DetallePaciente, no en el dashboard
// (el kine ya tiene que entrar para investigar por qué expiró).
//
// Sub-decisión: el trigger NO está dentro del Link wrapper del row. El
// caller (PacienteRow / PacienteCard) tiene que controlar el wrap del
// click — el menu maneja stopPropagation en su trigger para evitar
// que un click en kebab dispare navegación del row.

import { useNavigate } from "react-router";
import {
  MoreVertical,
  Eye,
  Plus,
  MessageCircle,
  Copy,
  MessageCircleMore,
  Check,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu";
import { getPacienteSignupLink } from "@/lib/auth/signupLink";

export interface QuickActionsPaciente {
  id: string;
  invitationStatus: "invited" | "active";
  signupToken: string | null;
}

interface Props {
  paciente: QuickActionsPaciente;
  /** Disparado cuando el kine elige "Compartir por WhatsApp" — el caller
   *  abre el InvitacionModal con el token. Solo aparece si invited. */
  onCompartirWhatsapp?: (signupToken: string) => void;
}

export function PatientQuickActionsMenu({
  paciente,
  onCompartirWhatsapp,
}: Props) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const canShowInvitation =
    paciente.invitationStatus === "invited" && paciente.signupToken !== null;

  const handleCopiar = async (e: Event) => {
    // Radix dispara onSelect con Event que NO es React synthetic — manejo
    // manual del prevent para mantener el menú abierto al copiar.
    e.preventDefault();
    if (!paciente.signupToken) return;
    const link = getPacienteSignupLink(paciente.signupToken);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("[PatientQuickActionsMenu] clipboard failed", err);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Más acciones"
          onClick={(e) => e.stopPropagation()}
          className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          <MoreVertical className="w-4 h-4" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        // Stop propagation para que click en items no dispare el row's Link wrapper.
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem
          onSelect={() => navigate(`/paciente/${paciente.id}`)}
          className="gap-2"
        >
          <Eye className="w-4 h-4" strokeWidth={1.5} />
          Ver detalle
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/prescribir/${paciente.id}`)}
          className="gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
          Prescribir rutina
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/paciente/${paciente.id}?tab=mensajes`)}
          className="gap-2"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          Enviar mensaje
        </DropdownMenuItem>

        {canShowInvitation && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleCopiar} className="gap-2">
              {copied ? (
                <Check className="w-4 h-4 text-primary" strokeWidth={1.5} />
              ) : (
                <Copy className="w-4 h-4" strokeWidth={1.5} />
              )}
              {copied ? "¡Copiado!" : "Copiar invitación"}
            </DropdownMenuItem>
            {onCompartirWhatsapp && (
              <DropdownMenuItem
                onSelect={() => onCompartirWhatsapp(paciente.signupToken!)}
                className="gap-2"
              >
                <MessageCircleMore className="w-4 h-4" strokeWidth={1.5} />
                Compartir por WhatsApp
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
